import { useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Alert, Button, Typography } from '@mui/material'
import { TextInput } from '../common/TextInput'
import { StepContent } from './StepContent'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { CreatorButtons } from './CreatorButtons'
import { CreatorSteps, markStepCompletedByName, nextStep } from '@/slices/creator'
import { RootState } from '@/store'
import { usePreferences } from '@/hooks/preferences/usePreferences'
import { IQuestion, ISecretsQuestionAnswer, MeetingType } from '@namuho/types'
import { useQuestions } from '@/hooks/questions/useQuestions'
import { SecretsForm } from '@/types/Questions'
import { SecretsSkeleton } from '@/skeletons/SecretsSkeleton'
import { isEmptyAnswer } from '@namuho/helpers'
import { StepTitle } from './StepTitle'

const useStyles = makeStyles()((theme) => ({
    form: {
        marginTop: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        width: '100%',
        [theme.breakpoints.down('md')]: {
            justifyContent: 'flex-start',
        },
    },
    alert: {
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
    },
}))

export interface Secret {
    id: string
    label: string
}

export interface SecretsAnswers {
    string: string
}

export const Secrets = () => {
    const { register, setValue, getValues } = useForm<SecretsForm>()
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const chosenDateType = useSelector((state: RootState) => state.creator.chosenDateType)
    const [currentSetOfSecrets, setCurrentSetOfSecrets] = useState<any[]>([])
    const [warningDissmissed, setWarningDissmissed] = useState(false)
    const [showWarning, setShowWarning] = useState(false)
    const { secrets, isLoading: isLoadingQuestions } = useQuestions()

    const [loading, setIsLoading] = useState(false)
    const {
        isLoading: isLoadingPreferences,
        friendlyPreferences,
        romanticPreferences,
        updateFriendlyPreferences,
        updateRomanticPreferences,
    } = usePreferences()

    const handleWarningDismiss = () => {
        setWarningDissmissed(true)
    }

    const handleNext = async () => {
        setIsLoading(true)
        const values = getValues()
        // answered questions
        const answeredQuestions = Object.keys(getValues()).filter((key: string) => !isEmptyAnswer(values[key]))
        if (!warningDissmissed && answeredQuestions.length < currentSetOfSecrets.length && answeredQuestions.length > 0) {
            setShowWarning(true)
            setIsLoading(false)
            return null
        }

        const answers: ISecretsQuestionAnswer[] = Object.keys(values).map((key) => ({
            questionId: key,
            answer: values[key],
        }))
        if (chosenDateType === MeetingType.Friendly) {
            await updateFriendlyPreferences({
                ...friendlyPreferences,
                secretQuestions: answers,
            })
        }
        if (chosenDateType === MeetingType.Romantic) {
            await updateRomanticPreferences({
                ...romanticPreferences,
                secretQuestions: answers,
            })
        }
        setIsLoading(false)
        if (answeredQuestions.length === currentSetOfSecrets.length && answeredQuestions.length > 0) {
            dispatch(markStepCompletedByName(CreatorSteps.SECRETS))
        }
        dispatch(nextStep())
    }

    useEffect(() => {
        try {
            if (friendlyPreferences) {
                const secretsAnswers = friendlyPreferences.secretQuestions
                secretsAnswers?.forEach((answer: ISecretsQuestionAnswer) => {
                    setValue(answer.questionId, answer.answer)
                })
            }
            if (romanticPreferences) {
                const secretsAnswers = romanticPreferences.secretQuestions
                secretsAnswers?.forEach((answer: ISecretsQuestionAnswer) => {
                    setValue(answer.questionId, answer.answer)
                })
            }
        } catch (e) {
            console.log(e)
        }
    }, [friendlyPreferences, romanticPreferences, setValue])

    useEffect(() => {
        if (secrets && secrets.length > 0) {
            const currentSecrets = secrets.filter((question: IQuestion) => question.meetingType === chosenDateType)
            setCurrentSetOfSecrets(currentSecrets)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chosenDateType])

    useEffect(() => {
        try {
            if (chosenDateType === MeetingType.Friendly) {
                const secretsAnswers = friendlyPreferences?.secretQuestions
                secretsAnswers?.forEach((answer: ISecretsQuestionAnswer) => {
                    setValue(answer.questionId, answer.answer)
                })
            }
            if (chosenDateType === MeetingType.Romantic) {
                const secretsAnswers = romanticPreferences?.secretQuestions
                secretsAnswers?.forEach((answer: ISecretsQuestionAnswer) => {
                    setValue(answer.questionId, answer.answer)
                })
            }
        } catch (e) {
            console.log(e)
        }
    }, [chosenDateType, friendlyPreferences, romanticPreferences, setValue])

    return (
        <StepContent
            title={<StepTitle title="Verrate uns deine Geheimnisse." />}
            content={
                <>
                    <Typography variant="body1">
                        Um Geheimnisse deiner {chosenDateType === MeetingType.Romantic ? 'Dates' : 'Treffen'} zu erfahren/freischalten musst du{' '}
                        <strong>Geheimnisse von dir übermitteln.</strong>{' '}
                        <a className="inline-link" rel="noreferrer" target="_blank" href="https://namuho.com/anleitung/#geheimnisse">
                            Mehr Informationen zu Geheimnissen
                        </a>
                    </Typography>
                    {(isLoadingPreferences || isLoadingQuestions) && <SecretsSkeleton />}
                    {currentSetOfSecrets && currentSetOfSecrets.length > 0 && (
                        <form className={classes.form}>
                            {currentSetOfSecrets.map((secret) => (
                                <TextInput key={secret.id} label={secret.label} {...register(secret.id)} />
                            ))}
                        </form>
                    )}
                    {showWarning && !warningDissmissed && (
                        <Alert
                            className={classes.alert}
                            severity="warning"
                            action={
                                <Button color="inherit" size="small" onClick={handleWarningDismiss}>
                                    OK
                                </Button>
                            }
                        >
                            Du hast nur einige geheime Fragen beantwortet. Um Zugang zu den Geheimnissen der Personen zu erhalten, mit denen du dich
                            treffen wirst, musst du alle Fragen beantworten.
                        </Alert>
                    )}
                </>
            }
            buttons={
                <CreatorButtons
                    nextStepActive={!loading}
                    previousStepActive={true}
                    isFirst={false}
                    isLast={false}
                    skipActive={true}
                    handleNext={handleNext}
                />
            }
        />
    )
}
